/**
 * Word / character counter — pure, deterministic text statistics.
 *
 * Reading speed 200 wpm, speaking speed 130 wpm (common conventions).
 */

const READING_WPM = 200;
const SPEAKING_WPM = 130;

const STOPWORDS = new Set([
  "the",
  "a",
  "an",
  "and",
  "or",
  "but",
  "of",
  "to",
  "in",
  "on",
  "for",
  "is",
  "are",
  "was",
  "were",
  "be",
  "been",
  "it",
  "its",
  "this",
  "that",
  "these",
  "those",
  "with",
  "as",
  "at",
  "by",
  "from",
  "he",
  "she",
  "they",
  "we",
  "you",
  "i",
  "his",
  "her",
  "their",
  "our",
  "my",
  "your",
  "not",
  "no",
  "so",
  "if",
  "then",
  "than",
  "too",
  "very",
  "can",
  "will",
  "just",
  "do",
  "does",
  "did",
]);

/** Strip leading/trailing non-letter/number characters from a token. */
function cleanWord(w: string): string {
  return w.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "");
}

export interface TextStats {
  characters: number;
  charactersNoSpaces: number;
  words: number;
  sentences: number;
  paragraphs: number;
  longestWord: string;
  avgWordLength: number;
  readingTimeSec: number;
  speakingTimeSec: number;
}

function words(text: string): string[] {
  const trimmed = text.trim();
  return trimmed === "" ? [] : trimmed.split(/\s+/);
}

export function countText(text: string): TextStats {
  const wordList = words(text);
  const cleaned = wordList.map(cleanWord).filter(Boolean);
  const totalLetters = cleaned.reduce((sum, w) => sum + w.length, 0);
  const longest = cleaned.reduce((a, b) => (b.length > a.length ? b : a), "");

  const sentences = text
    .split(/[.!?]+/)
    .map((s) => s.trim())
    .filter(Boolean).length;
  const paragraphs = text
    .split(/\n{2,}/)
    .map((s) => s.trim())
    .filter(Boolean).length;

  return {
    characters: text.length,
    charactersNoSpaces: text.replace(/\s/g, "").length,
    words: wordList.length,
    sentences,
    paragraphs,
    longestWord: longest,
    avgWordLength:
      cleaned.length === 0
        ? 0
        : Math.round((totalLetters / cleaned.length) * 10) / 10,
    readingTimeSec: Math.round((wordList.length / READING_WPM) * 60),
    speakingTimeSec: Math.round((wordList.length / SPEAKING_WPM) * 60),
  };
}

export interface KeywordCount {
  word: string;
  count: number;
  percent: number;
}

/** Top-N most frequent non-stopword keywords; percent is of all words. */
export function keywordDensity(text: string, topN = 5): KeywordCount[] {
  const wordList = words(text);
  const total = wordList.length;
  if (total === 0) return [];

  const counts = new Map<string, number>();
  for (const raw of wordList) {
    const w = cleanWord(raw).toLowerCase();
    if (!w || STOPWORDS.has(w)) continue;
    counts.set(w, (counts.get(w) ?? 0) + 1);
  }

  return [...counts.entries()]
    .map(([word, count]) => ({
      word,
      count,
      percent: Math.round((count / total) * 1000) / 10,
    }))
    .sort((a, b) => b.count - a.count || a.word.localeCompare(b.word))
    .slice(0, topN);
}
